// services/templatesApi.js - Полная интеграция с новым backend API
import { authStorage } from '../utils/storage';

// =============================================================================
// КОНФИГУРАЦИЯ API
// =============================================================================
const BACKEND_CONFIG = {
  /**
   * BASE_URL:
   * - В development всегда ходим прямо на backend:
   *     "http://localhost:7000/api"
   * - В production берем из REACT_APP_API_URL (например "https://dogovor-builder.ru/api")
   *   либо по умолчанию "https://dogovor-builder.ru/api"
   */
  BASE_URL:
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:7000/api'
      : (process.env.REACT_APP_API_URL || 'https://dogovor-builder.ru/api'),
  TIMEOUT: 15000,
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// =============================================================================
// БАЗОВЫЕ ФУНКЦИИ ДЛЯ API ЗАПРОСОВ
// =============================================================================
const createApiResponse = (data, error = null, message = '') => {
  return {
    status: error ? 'error' : 'success',
    data,
    error,
    message,
    timestamp: new Date().toISOString()
  };
};

const createApiError = (type, message, details = {}) => {
  return {
    type,
    message,
    details,
    timestamp: new Date().toISOString()
  };
};

const makeRequest = async (endpoint, options = {}) => {
  const url = `${BACKEND_CONFIG.BASE_URL}${endpoint}`;
  const config = {
    method: 'GET',
    headers: { ...BACKEND_CONFIG.HEADERS },
    ...options
  };

  // Добавляем токен авторизации если есть
  const token = authStorage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Добавляем тело запроса для POST/PUT
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BACKEND_CONFIG.TIMEOUT);

    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    let responseData;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      if (response.status === 401) {
        authStorage.clearAuthData();
      }
      
      throw createApiError(
        response.status === 401 ? 'AUTH_ERROR' :
        response.status === 400 ? 'VALIDATION_ERROR' :
        response.status === 404 ? 'NOT_FOUND' :
        response.status === 409 ? 'CONFLICT' : 'SERVER_ERROR',
        responseData?.error || responseData?.message || `HTTP ${response.status}`,
        { status: response.status, response: responseData }
      );
    }

    return createApiResponse(responseData, null, responseData?.message || 'Success');

  } catch (error) {
    if (error.name === 'AbortError') {
      throw createApiError('NETWORK_ERROR', 'Запрос превысил время ожидания');
    }
    if (error.type) {
      throw error; // Уже обработанная ошибка API
    }
    throw createApiError(
      'NETWORK_ERROR',
      'Ошибка сети. Проверьте подключение к интернету.',
      { originalError: error.message }
    );
  }
};

// =============================================================================
// TEMPLATES API
// =============================================================================
export const templatesApi = {
  /**
   * Получение каталога всех шаблонов
   * GET /api/templates?type={type}
   */
  getTemplates: async (type = null) => {
    console.log('API getTemplates called with type:', type);
    const endpoint = type ? `/templates?type=${encodeURIComponent(type)}` : '/templates';
    return await makeRequest(endpoint);
  },

  /**
   * Получение детальной информации о шаблоне
   * GET /api/templates/{id}
   */
  getTemplate: async (templateId) => {
    console.log('API getTemplate called with:', templateId);
    return await makeRequest(`/templates/${templateId}`);
  },

  /**
   * Получение полей формы для предпросмотра шаблона
   * GET /api/templates/{id}/preview
   */
  getTemplatePreview: async (templateId) => {
    console.log('API getTemplatePreview called:', templateId);
    return await makeRequest(`/templates/${templateId}/preview`);
  },

  /**
   * Проверка доступа пользователя к шаблону (куплен ли)
   * GET /api/templates/{id}/access [Authorize]
   */
  checkTemplateAccess: async (templateId) => {
    console.log('API checkTemplateAccess called:', templateId);
    return await makeRequest(`/templates/${templateId}/access`);
  },

  /**
   * Валидация данных шаблона
   * POST /api/templates/validate
   */
  validateTemplateData: async (templateId, data) => {
    console.log('API validateTemplateData called:', templateId);
    return await makeRequest('/templates/validate', {
      method: 'POST',
      body: { templateId, data }
    });
  },

  /**
   * Получение категорий шаблонов
   * GET /api/templates/categories
   */
  getTemplateCategories: async () => {
    console.log('API getTemplateCategories called');
    return await makeRequest('/templates/categories');
  },

  /**
   * Получение популярных шаблонов
   * GET /api/templates/popular?limit=5
   */
  getPopularTemplates: async (limit = 5) => {
    console.log('API getPopularTemplates called, limit:', limit);
    return await makeRequest(`/templates/popular?limit=${limit}`);
  },

  /**
   * Поиск шаблонов
   * GET /api/templates/search?query={query}
   */
  searchTemplates: async (query) => {
    console.log('API searchTemplates called, query:', query);
    return await makeRequest(`/templates/search?query=${encodeURIComponent(query)}`);
  }
};

// =============================================================================
// PURCHASES API
// =============================================================================
export const purchasesApi = {
  /**
   * Получение покупок пользователя
   * GET /api/payments/purchases [Authorize]
   */
  getUserPurchases: async () => {
    console.log('API getUserPurchases called');
    return await makeRequest('/payments/purchases');
  },

  /**
   * Проверка покупки конкретного шаблона
   * GET /api/payments/check/{templateId} [Authorize]
   */
  checkTemplatePurchase: async (templateId) => {
    console.log('API checkTemplatePurchase called:', templateId);
    return await makeRequest(`/payments/check/${templateId}`);
  },

  /**
   * Создание платежа за шаблон
   * POST /api/payments/create [Authorize]
   */
  createPayment: async (templateId, email ) => {
    console.log('API createPayment called:', { templateId });
    return await makeRequest('/payments/create', {
      method: 'POST',
      body: { templateId, email }
    });
  },

  /**
   * Проверка статуса платежа
   * GET /api/payments/status/{paymentId} [Authorize]
   */
  getPaymentStatus: async (paymentId) => {
    console.log('API getPaymentStatus called:', paymentId);
    return await makeRequest(`/payments/status/${paymentId}`);
  }
};

// =============================================================================
// USER CONTRACTS API
// =============================================================================
export const userContractsApi = {
  /**
   * Получение договоров пользователя
   * GET /api/contracts?page=1&pageSize=10 [Authorize]
   */
  getUserContracts: async (page = 1, pageSize = 50) => {
    console.log('API getUserContracts called');
    return await makeRequest(`/contracts?page=${page}&pageSize=${pageSize}`);
  },

  /**
   * Получение конкретного договора
   * GET /api/contracts/{id} [Authorize]
   */
  getContract: async (contractId) => {
    console.log('API getContract called:', contractId);
    return await makeRequest(`/contracts/${contractId}`);
  },

  /**
   * Создание нового договора
   * POST /api/contracts [Authorize]
   */
  createContract: async (templateId, data) => {
    console.log('API createContract called:', { templateId, data });
    return await makeRequest('/contracts', {
      method: 'POST',
      body: { templateId, data }
    });
  },

  /**
   * Обновление существующего договора
   * PUT /api/contracts/{id} [Authorize]
   */
  updateContract: async (contractId, data) => {
    console.log('API updateContract called:', contractId);
    return await makeRequest(`/contracts/${contractId}`, {
      method: 'PUT',
      body: { data }
    });
  },

  /**
   * Удаление договора
   * DELETE /api/contracts/{id} [Authorize]
   */
  deleteContract: async (contractId) => {
    console.log('API deleteContract called:', contractId);
    return await makeRequest(`/contracts/${contractId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Скачивание договора в указанном формате
   * GET /api/contracts/{id}/download/{format} [Authorize]
   * Возвращает { blob, filename } — filename из Content-Disposition или сформированное.
   */
  downloadContract: async (contractId, format = 'word') => {
    const url = `${BACKEND_CONFIG.BASE_URL}/contracts/${contractId}/download/${format}`;
    const token = authStorage.getToken();
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Ошибка при скачивании документа');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = null;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
        || contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1].trim());
      }
    }
    const ext = format === 'word' ? 'docx' : 'pdf';
    if (!filename) {
      const now = new Date();
      const date = `${String(now.getDate()).padStart(2, '0')}_${String(now.getMonth() + 1).padStart(2, '0')}_${now.getFullYear()}`;
      filename = `Договор_${date}.${ext}`;
    }

    const blob = await response.blob();
    return { blob, filename };
  },
};

// =============================================================================
// ОСНОВНОЙ API КЛАСС
// =============================================================================
class TemplatesApiService {
  // Методы для работы с шаблонами
  getTemplates = templatesApi.getTemplates;
  getTemplate = templatesApi.getTemplate;
  getTemplatePreview = templatesApi.getTemplatePreview;
  checkTemplateAccess = templatesApi.checkTemplateAccess;
  validateTemplateData = templatesApi.validateTemplateData;
  getTemplateCategories = templatesApi.getTemplateCategories;
  getPopularTemplates = templatesApi.getPopularTemplates;
  searchTemplates = templatesApi.searchTemplates;

  // Методы для работы с покупками
  getUserPurchases = purchasesApi.getUserPurchases;
  checkTemplatePurchase = purchasesApi.checkTemplatePurchase;
  createPayment = purchasesApi.createPayment;
  getPaymentStatus = purchasesApi.getPaymentStatus;

  // Методы для работы с договорами пользователей
  getUserContracts = userContractsApi.getUserContracts;
  getContract = userContractsApi.getContract;
  createContract = userContractsApi.createContract;
  updateContract = userContractsApi.updateContract;
  deleteContract = userContractsApi.deleteContract;
  downloadContract = userContractsApi.downloadContract;

  // Вспомогательные методы
  getToken() {
    return authStorage.getToken();
  }

  isAuthenticated() {
    return authStorage.isTokenValid();
  }

  // Универсальный метод для скачивания документов с автоматическим сохранением
  // suggestedFilename — опционально: если передан (например из ЛК), используется вместо имени из ответа
  downloadContractFile = async (contractId, format = 'word', suggestedFilename = null) => {
    try {
      const { blob, filename } = await this.downloadContract(contractId, format);
      const finalFilename = suggestedFilename || filename;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFilename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(url);
      
      return createApiResponse({
        success: true,
        message: 'Файл успешно скачан',
        fileName: finalFilename
      });
    } catch (error) {
      console.error('Ошибка скачивания документа:', error);
      throw error;
    }
  };

  // Метод для обработки ошибок с пользовательскими сообщениями
  handleApiError = (error) => {
    console.error('API Error:', error);
    
    if (error.type === 'AUTH_ERROR') {
      return 'Необходима повторная авторизация. Войдите в систему.';
    } else if (error.type === 'VALIDATION_ERROR') {
      return error.message || 'Ошибка валидации данных.';
    } else if (error.type === 'NOT_FOUND') {
      return 'Запрашиваемый ресурс не найден.';
    } else if (error.type === 'CONFLICT') {
      return error.message || 'Конфликт данных. Возможно, ресурс уже существует.';
    } else if (error.type === 'NETWORK_ERROR') {
      return 'Ошибка сети. Проверьте подключение к интернету.';
    } else {
      return error.message || 'Произошла неизвестная ошибка.';
    }
  };
}

// Создаем и экспортируем единственный экземпляр
const templatesApiService = new TemplatesApiService();

export default templatesApiService;